import { useState, useEffect } from 'react';
import {
  Card, Typography, Switch, Button, message, Space, Divider,
  Input, InputNumber, Tabs, Select
} from 'antd';
import {
  BellOutlined,
  EnvironmentOutlined,
  SaveOutlined,
  WarningOutlined,
  DesktopOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { supabase, usePortalPermissions, PermissionGate } from '@ubus/shared';
import MapSettingsTab from '../components/MapSettingsTab';
import AdminSectionPage from '../components/AdminSectionPage';
import { useTranslation } from 'react-i18next';
import { useDisplayTimezone } from '../components/DisplayTimezoneProvider';
import {
  ADMIN_TIMEZONE_OPTIONS,
  browserTimeZoneId,
  type AdminDisplayTimezone,
} from '../utils/displayTimezone';

const { Text } = Typography;

// 设置类型
interface NotificationSettings {
  sos_email_alerts: boolean;
  sos_sms_alerts: boolean;
  sos_alert_emails: string[];
  sos_alert_phones: string[];
  trip_reminder_enabled: boolean;
  trip_reminder_minutes: number;
}

interface SystemSettings {
  gps_report_interval: number;
  delay_threshold_minutes: number;
  maintenance_reminder_days: number;
  travel_time_mode: 'static' | 'directions';
  static_average_speed_kmh: number;
  travel_road_factor: number;
  /** Minutes added to earliest_start to derive default latest_start on job legs. */
  latest_start_offset_minutes: number;
  /** Customer trip notify (CONF-20260729). */
  customer_notify_on_assign_sms: boolean;
  customer_notify_on_assign_email: boolean;
  customer_notify_pre_trip_sms: boolean;
  customer_notify_pre_trip_email: boolean;
  customer_pre_trip_notify_minutes: number;
  map_provider: 'google';
  google_maps_api_key: string;
}

function linesToList(text: string): string[] {
  return text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

function Settings() {
  const { t } = useTranslation();
  const { timezone, setTimezone } = useDisplayTimezone();
  const { hasPermission } = usePortalPermissions();
  const canEdit = hasPermission('settings.edit');
  const [activeTab, setActiveTab] = useState('display');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mapSavedAt, setMapSavedAt] = useState<string | null>(null);

  // 通知设置
  const [notifSettings, setNotifSettings] = useState<NotificationSettings>({
    sos_email_alerts: true,
    sos_sms_alerts: false,
    sos_alert_emails: ['admin@ubus.com.au'],
    sos_alert_phones: [],
    trip_reminder_enabled: true,
    trip_reminder_minutes: 30
  });
  // Draft multiline text — parse only on save (avoid filter-on-keystroke breaking Enter / typing)
  const [sosEmailsText, setSosEmailsText] = useState('admin@ubus.com.au');
  const [sosPhonesText, setSosPhonesText] = useState('');

  // 系统设置
  const [sysSettings, setSysSettings] = useState<SystemSettings>({
    gps_report_interval: 120,
    delay_threshold_minutes: 30,
    maintenance_reminder_days: 90,
    travel_time_mode: 'static',
    static_average_speed_kmh: 35,
    travel_road_factor: 1.25,
    latest_start_offset_minutes: 30,
    customer_notify_on_assign_sms: true,
    customer_notify_on_assign_email: false,
    customer_notify_pre_trip_sms: true,
    customer_notify_pre_trip_email: false,
    customer_pre_trip_notify_minutes: 60,
    map_provider: 'google',
    google_maps_api_key: '',
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      // 从数据库加载设置
      const { data: notifData } = await supabase
        .from('settings')
        .select('key, value')
        .eq('key', 'notification_settings')
        .single();

      const { data: sysData } = await supabase
        .from('settings')
        .select('key, value, updated_at')
        .eq('key', 'system_settings')
        .single();

      if (notifData?.value) {
        const v = notifData.value as Partial<NotificationSettings>;
        const emails = Array.isArray(v.sos_alert_emails) ? v.sos_alert_emails : ['admin@ubus.com.au'];
        const phones = Array.isArray(v.sos_alert_phones) ? v.sos_alert_phones : [];
        setNotifSettings((prev) => ({ ...prev, ...v, sos_alert_emails: emails, sos_alert_phones: phones }));
        setSosEmailsText(emails.join('\n'));
        setSosPhonesText(phones.join('\n'));
      } else {
        const { data: legacyPhones } = await supabase
          .from('settings')
          .select('value')
          .eq('key', 'admin_phones')
          .maybeSingle();

        if (Array.isArray(legacyPhones?.value) && legacyPhones.value.length > 0) {
          const phones = legacyPhones.value as string[];
          setNotifSettings((prev) => ({
            ...prev,
            sos_alert_phones: phones,
          }));
          setSosPhonesText(phones.join('\n'));
        }
      }
      if (sysData?.value && typeof sysData.value === 'object') {
        const v = sysData.value as Record<string, unknown>;
        setSysSettings({
          gps_report_interval: typeof v.gps_report_interval === 'number' ? v.gps_report_interval : 120,
          delay_threshold_minutes: typeof v.delay_threshold_minutes === 'number' ? v.delay_threshold_minutes : 30,
          maintenance_reminder_days: typeof v.maintenance_reminder_days === 'number' ? v.maintenance_reminder_days : 90,
          travel_time_mode: v.travel_time_mode === 'directions' ? 'directions' : 'static',
          static_average_speed_kmh: typeof v.static_average_speed_kmh === 'number' ? v.static_average_speed_kmh : 35,
          travel_road_factor: typeof v.travel_road_factor === 'number' ? v.travel_road_factor : 1.25,
          latest_start_offset_minutes:
            typeof v.latest_start_offset_minutes === 'number' ? v.latest_start_offset_minutes : 30,
          customer_notify_on_assign_sms: v.customer_notify_on_assign_sms !== false,
          customer_notify_on_assign_email: v.customer_notify_on_assign_email === true,
          customer_notify_pre_trip_sms: v.customer_notify_pre_trip_sms !== false,
          customer_notify_pre_trip_email: v.customer_notify_pre_trip_email === true,
          customer_pre_trip_notify_minutes:
            typeof v.customer_pre_trip_notify_minutes === 'number'
              ? Math.max(5, Math.min(240, v.customer_pre_trip_notify_minutes))
              : 60,
          map_provider: 'google',
          google_maps_api_key: typeof v.google_maps_api_key === 'string' ? v.google_maps_api_key : '',
        });
        if (typeof sysData.updated_at === 'string') {
          setMapSavedAt(sysData.updated_at);
        }
      }

    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveNotificationSettings = async () => {
    setSaving(true);
    try {
      const next: NotificationSettings = {
        ...notifSettings,
        sos_alert_emails: linesToList(sosEmailsText),
        sos_alert_phones: linesToList(sosPhonesText),
      };
      setNotifSettings(next);
      setSosEmailsText(next.sos_alert_emails.join('\n'));
      setSosPhonesText(next.sos_alert_phones.join('\n'));

      const { error } = await supabase
        .from('settings')
        .upsert(
          {
            key: 'notification_settings',
            value: next,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'key' }
        );

      if (error) throw error;

      const { error: legacyError } = await supabase.from('settings').upsert(
        {
          key: 'admin_phones',
          value: next.sos_alert_phones,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'key' }
      );

      if (legacyError) throw legacyError;
      message.success(t('settings.saveNotificationSuccess'));
    } catch (err: any) {
      message.error(t('settings.saveFailed', { message: err.message }));
    } finally {
      setSaving(false);
    }
  };

  const saveSystemSettings = async () => {
    setSaving(true);
    try {
      const updatedAt = new Date().toISOString();
      const { error } = await supabase
        .from('settings')
        .upsert(
          {
            key: 'system_settings',
            value: {
              ...sysSettings,
              map_provider: 'google',
              amap_api_key: '',
              amap_security_key: '',
            },
            updated_at: updatedAt,
          },
          { onConflict: 'key' }
        );

      if (error) throw error;
      message.success(t('settings.saveSystemSuccess'));
    } catch (err: any) {
      message.error(t('settings.saveFailed', { message: err.message }));
    } finally {
      setSaving(false);
    }
  };

  const saveMapSettings = async () => {
    setSaving(true);
    try {
      const updatedAt = new Date().toISOString();
      const value = {
        ...sysSettings,
        map_provider: 'google' as const,
        // Clear legacy AMap keys if still present in stored JSON.
        amap_api_key: '',
        amap_security_key: '',
      };
      const { error } = await supabase
        .from('settings')
        .upsert(
          {
            key: 'system_settings',
            value,
            updated_at: updatedAt,
          },
          { onConflict: 'key' }
        );

      if (error) throw error;
      setMapSavedAt(updatedAt);
      message.success(t('settings.saveMapSuccess'));
    } catch (err: any) {
      message.error(t('settings.saveFailed', { message: err.message }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminSectionPage title={t('settings.title')} subtitle={t('settings.subtitle')} loading />
    );
  }

  return (
    <AdminSectionPage
      title={t('settings.title')}
      subtitle={t('settings.subtitle')}
      bare
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        tabPosition="left"
        className="admin-settings-tabs"
        style={{ minHeight: 400 }}
        items={[
          {
            key: 'display',
            label: (
              <span>
                <DesktopOutlined /> {t('settings.display')}
              </span>
            ),
            children: (
              <Card className="admin-inner-card" title={t('settings.displaySettings')}>
                <div className="admin-form-measure">
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <Text strong>{t('settings.timezone')}</Text>
                    <br />
                    <Text type="secondary">{t('settings.timezoneHelp')}</Text>
                    <div style={{ marginTop: 12 }}>
                      <Select
                        style={{ width: '100%' }}
                        value={timezone}
                        onChange={(val: AdminDisplayTimezone) => {
                          setTimezone(val);
                          message.success(t('settings.tzSaved'));
                        }}
                        options={ADMIN_TIMEZONE_OPTIONS.map((o) => ({
                          value: o.value,
                          label: t(o.labelKey),
                        }))}
                      />
                    </div>
                    {timezone === 'local' ? (
                      <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                        {t('settings.tzBrowserDetected', { zone: browserTimeZoneId() })}
                      </Text>
                    ) : null}
                  </div>
                </Space>
                </div>
              </Card>
            ),
          },
          {
            key: 'notifications',
            label: <span><BellOutlined /> {t('settings.notifications')}</span>,
            children: (
              <Card className="admin-inner-card" title={<span><BellOutlined style={{ marginRight: 8 }} />{t('settings.notificationSettings')}</span>}>
                <div className="admin-form-measure">
                <div className="admin-settings-section">
                  <Card
                    type="inner"
                    className="admin-inner-card"
                    title={<span><WarningOutlined style={{ color: 'var(--color-destructive)', marginRight: 8 }} />{t('settings.sosAlertSettings')}</span>}
                  >
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      <div className="admin-settings-row">
                        <div className="admin-settings-row__label">
                          <Text strong>{t('settings.emailNotifications')}</Text>
                          <br />
                          <Text type="secondary">{t('settings.emailHelp')}</Text>
                        </div>
                        <div className="admin-settings-row__control">
                        <Switch
                          checked={notifSettings.sos_email_alerts}
                          disabled={!canEdit}
                          onChange={(checked) => setNotifSettings(prev => ({ ...prev, sos_email_alerts: checked }))}
                        />
                        </div>
                      </div>

                      <Divider style={{ margin: '8px 0' }} />

                      {notifSettings.sos_email_alerts && (
                        <>
                          <div>
                            <Text strong>{t('settings.sosEmails')}</Text>
                            <Text type="secondary" style={{ marginLeft: 8 }}>{t('settings.onePerLine')}</Text>
                            <Input.TextArea
                              value={sosEmailsText}
                              onChange={(e) => setSosEmailsText(e.target.value)}
                              disabled={!canEdit}
                              rows={4}
                              placeholder="admin@example.com&#10;ops@example.com"
                              style={{ marginTop: 8 }}
                            />
                          </div>
                        </>
                      )}

                      <Divider style={{ margin: '8px 0' }} />

                      <div className="admin-settings-row">
                        <div className="admin-settings-row__label">
                          <Text strong>{t('settings.smsNotifications')}</Text>
                          <br />
                          <Text type="secondary">{t('settings.smsHelp')}</Text>
                        </div>
                        <div className="admin-settings-row__control">
                        <Switch
                          checked={notifSettings.sos_sms_alerts}
                          disabled={!canEdit}
                          onChange={(checked) => setNotifSettings(prev => ({ ...prev, sos_sms_alerts: checked }))}
                        />
                        </div>
                      </div>

                      {notifSettings.sos_sms_alerts && (
                        <div style={{ marginTop: 8 }}>
                          <Text strong>{t('settings.phoneNumbers')}</Text>
                          <Input.TextArea
                            value={sosPhonesText}
                            onChange={(e) => setSosPhonesText(e.target.value)}
                            disabled={!canEdit}
                            rows={3}
                            placeholder="+61412345678&#10;+61498765432"
                            style={{ marginTop: 8 }}
                          />
                        </div>
                      )}
                    </Space>
                  </Card>

                  <Card
                    type="inner"
                    className="admin-inner-card"
                    title={<span><BellOutlined style={{ color: 'var(--color-primary)', marginRight: 8 }} />{t('settings.tripReminders')}</span>}
                  >
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      <div className="admin-settings-row">
                        <div className="admin-settings-row__label">
                          <Text strong>{t('settings.enableReminders')}</Text>
                          <br />
                          <Text type="secondary">{t('settings.reminderHelp')}</Text>
                        </div>
                        <div className="admin-settings-row__control">
                        <Switch
                          checked={notifSettings.trip_reminder_enabled}
                          disabled={!canEdit}
                          onChange={(checked) => setNotifSettings(prev => ({ ...prev, trip_reminder_enabled: checked }))}
                        />
                        </div>
                      </div>

                      {notifSettings.trip_reminder_enabled && (
                        <div>
                          <Text strong>{t('settings.reminderTime')}</Text>
                          <InputNumber
                            min={5}
                            max={120}
                            step={5}
                            value={notifSettings.trip_reminder_minutes}
                            disabled={!canEdit}
                            onChange={(val) => setNotifSettings(prev => ({ ...prev, trip_reminder_minutes: val || 30 }))}
                            addonAfter={t('settings.minutes')}
                            style={{ marginTop: 8, width: 200 }}
                          />
                        </div>
                      )}
                    </Space>
                  </Card>

                  <PermissionGate permission="settings.edit">
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      onClick={saveNotificationSettings}
                      loading={saving}
                      size="large"
                      block
                    >
                      {t('settings.saveNotifications')}
                    </Button>
                  </PermissionGate>
                </div>
                </div>
              </Card>
            )
          },
          {
            key: 'maps',
            label: <span><EnvironmentOutlined /> {t('settings.maps')}</span>,
            children: (
              <MapSettingsTab
                value={{
                  map_provider: 'google',
                  google_maps_api_key: sysSettings.google_maps_api_key,
                }}
                onChange={(mapFields) =>
                  setSysSettings((prev) => ({
                    ...prev,
                    map_provider: 'google',
                    google_maps_api_key: mapFields.google_maps_api_key,
                  }))
                }
                onSave={canEdit ? saveMapSettings : async () => {
                  message.warning(t('rbac.saveFailed'));
                }}
                saving={saving}
                lastSavedAt={mapSavedAt}
              />
            ),
          },
          {
            key: 'system',
            label: <span><SettingOutlined /> {t('settings.system')}</span>,
            children: (
              <Card className="admin-inner-card" title={t('settings.systemSettings')}>
                <div className="admin-form-measure">
                  <div className="admin-settings-section">
                    <Card type="inner" className="admin-inner-card" title={t('settings.sectionOps')}>
                      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <div>
                          <Text strong>{t('settings.reportInterval')}</Text>
                          <br />
                          <Text type="secondary">{t('settings.reportHelp')}</Text>
                          <div style={{ marginTop: 8 }}>
                            <InputNumber
                              min={30}
                              max={600}
                              step={30}
                              value={sysSettings.gps_report_interval}
                              onChange={(val) => setSysSettings((prev) => ({ ...prev, gps_report_interval: val || 120 }))}
                              addonAfter={t('settings.seconds')}
                              style={{ width: 240 }}
                            />
                          </div>
                        </div>
                        <Divider style={{ margin: '4px 0' }} />
                        <div>
                          <Text strong>{t('settings.delayThreshold')}</Text>
                          <br />
                          <Text type="secondary">{t('settings.delayHelp')}</Text>
                          <div style={{ marginTop: 8 }}>
                            <InputNumber
                              min={5}
                              max={120}
                              step={5}
                              value={sysSettings.delay_threshold_minutes}
                              onChange={(val) => setSysSettings((prev) => ({ ...prev, delay_threshold_minutes: val || 30 }))}
                              addonAfter={t('settings.minutes')}
                              style={{ width: 240 }}
                            />
                          </div>
                        </div>
                        <Divider style={{ margin: '4px 0' }} />
                        <div>
                          <Text strong>{t('settings.maintenanceReminder')}</Text>
                          <br />
                          <Text type="secondary">{t('settings.maintenanceHelp')}</Text>
                          <div style={{ marginTop: 8 }}>
                            <InputNumber
                              min={7}
                              max={365}
                              step={7}
                              value={sysSettings.maintenance_reminder_days}
                              onChange={(val) => setSysSettings((prev) => ({ ...prev, maintenance_reminder_days: val || 90 }))}
                              addonAfter={t('settings.days')}
                              style={{ width: 240 }}
                            />
                          </div>
                        </div>
                      </Space>
                    </Card>

                    <Card type="inner" className="admin-inner-card" title={t('settings.sectionSchedule')}>
                      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <div>
                          <Text strong>{t('settings.travelTimeMode')}</Text>
                          <br />
                          <Text type="secondary">{t('settings.travelTimeHelp')}</Text>
                          <div style={{ marginTop: 12 }}>
                            <Space wrap>
                              <Button
                                type={sysSettings.travel_time_mode === 'static' ? 'primary' : 'default'}
                                onClick={() => setSysSettings((prev) => ({ ...prev, travel_time_mode: 'static' }))}
                              >
                                {t('settings.staticEstimate')}
                              </Button>
                              <Button
                                type={sysSettings.travel_time_mode === 'directions' ? 'primary' : 'default'}
                                onClick={() =>
                                  setSysSettings((prev) => ({ ...prev, travel_time_mode: 'directions' }))
                                }
                              >
                                {t('settings.directionsApi')}
                              </Button>
                            </Space>
                          </div>
                          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                            {t('settings.staticEstimateHelp')}
                          </Text>
                          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                            {t('settings.directionsApiHelp')}
                          </Text>
                        </div>
                        <Divider style={{ margin: '4px 0' }} />
                        <div>
                          <Text strong>{t('settings.averageSpeed')}</Text>
                          <br />
                          <Text type="secondary">{t('settings.averageSpeedHelp')}</Text>
                          <div style={{ marginTop: 8 }}>
                            <InputNumber
                              min={15}
                              max={80}
                              step={5}
                              value={sysSettings.static_average_speed_kmh}
                              onChange={(val) => setSysSettings((prev) => ({ ...prev, static_average_speed_kmh: val || 35 }))}
                              addonAfter="km/h"
                              style={{ width: 240 }}
                              disabled={sysSettings.travel_time_mode !== 'static'}
                            />
                          </div>
                        </div>
                        <div>
                          <Text strong>{t('settings.roadFactor')}</Text>
                          <br />
                          <Text type="secondary">{t('settings.roadFactorHelp')}</Text>
                          <div style={{ marginTop: 8 }}>
                            <InputNumber
                              min={1}
                              max={2}
                              step={0.05}
                              value={sysSettings.travel_road_factor}
                              onChange={(val) => setSysSettings((prev) => ({ ...prev, travel_road_factor: val || 1.25 }))}
                              style={{ width: 240 }}
                              disabled={sysSettings.travel_time_mode !== 'static'}
                            />
                          </div>
                        </div>
                        <Divider style={{ margin: '4px 0' }} />
                        <div>
                          <Text strong>{t('settings.latestStartOffset')}</Text>
                          <br />
                          <Text type="secondary">{t('settings.latestStartOffsetHelp')}</Text>
                          <div style={{ marginTop: 8 }}>
                            <InputNumber
                              min={0}
                              max={240}
                              step={5}
                              value={sysSettings.latest_start_offset_minutes}
                              onChange={(val) =>
                                setSysSettings((prev) => ({
                                  ...prev,
                                  latest_start_offset_minutes: val ?? 0,
                                }))
                              }
                              addonAfter={t('settings.minutes')}
                              style={{ width: 240 }}
                            />
                          </div>
                        </div>
                      </Space>
                    </Card>

                    <Card type="inner" className="admin-inner-card" title={t('settings.customerTripNotify')}>
                      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <Text type="secondary">{t('settings.customerTripNotifyHelp')}</Text>
                        <div className="admin-settings-row">
                          <div className="admin-settings-row__label">
                            <Text strong>{t('settings.assignSms')}</Text>
                            <br />
                            <Text type="secondary">{t('settings.assignSmsHelp')}</Text>
                          </div>
                          <div className="admin-settings-row__control">
                            <Switch
                              checked={sysSettings.customer_notify_on_assign_sms}
                              disabled={!canEdit}
                              onChange={(checked) =>
                                setSysSettings((prev) => ({
                                  ...prev,
                                  customer_notify_on_assign_sms: checked,
                                }))
                              }
                            />
                          </div>
                        </div>
                        <div className="admin-settings-row">
                          <div className="admin-settings-row__label">
                            <Text strong>{t('settings.assignEmail')}</Text>
                            <br />
                            <Text type="secondary">{t('settings.assignEmailHelp')}</Text>
                          </div>
                          <div className="admin-settings-row__control">
                            <Switch
                              checked={sysSettings.customer_notify_on_assign_email}
                              disabled={!canEdit}
                              onChange={(checked) =>
                                setSysSettings((prev) => ({
                                  ...prev,
                                  customer_notify_on_assign_email: checked,
                                }))
                              }
                            />
                          </div>
                        </div>
                        <Divider style={{ margin: '4px 0' }} />
                        <div className="admin-settings-row">
                          <div className="admin-settings-row__label">
                            <Text strong>{t('settings.preTripSms')}</Text>
                            <br />
                            <Text type="secondary">{t('settings.preTripSmsHelp')}</Text>
                          </div>
                          <div className="admin-settings-row__control">
                            <Switch
                              checked={sysSettings.customer_notify_pre_trip_sms}
                              disabled={!canEdit}
                              onChange={(checked) =>
                                setSysSettings((prev) => ({
                                  ...prev,
                                  customer_notify_pre_trip_sms: checked,
                                }))
                              }
                            />
                          </div>
                        </div>
                        <div className="admin-settings-row">
                          <div className="admin-settings-row__label">
                            <Text strong>{t('settings.preTripEmail')}</Text>
                            <br />
                            <Text type="secondary">{t('settings.preTripEmailHelp')}</Text>
                          </div>
                          <div className="admin-settings-row__control">
                            <Switch
                              checked={sysSettings.customer_notify_pre_trip_email}
                              disabled={!canEdit}
                              onChange={(checked) =>
                                setSysSettings((prev) => ({
                                  ...prev,
                                  customer_notify_pre_trip_email: checked,
                                }))
                              }
                            />
                          </div>
                        </div>
                        <div>
                          <Text strong>{t('settings.preTripMinutes')}</Text>
                          <br />
                          <Text type="secondary">{t('settings.preTripMinutesHelp')}</Text>
                          <div style={{ marginTop: 8 }}>
                            <InputNumber
                              min={5}
                              max={240}
                              step={5}
                              value={sysSettings.customer_pre_trip_notify_minutes}
                              disabled={!canEdit}
                              onChange={(val) =>
                                setSysSettings((prev) => ({
                                  ...prev,
                                  customer_pre_trip_notify_minutes: val ?? 60,
                                }))
                              }
                              addonAfter={t('settings.minutes')}
                              style={{ width: 240 }}
                            />
                          </div>
                        </div>
                        <Text type="secondary">{t('settings.customerTripTemplatesHint')}</Text>
                      </Space>
                    </Card>

                    <PermissionGate permission="settings.edit">
                      <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={saveSystemSettings}
                        loading={saving}
                        size="large"
                        block
                      >
                        {t('settings.saveSystem')}
                      </Button>
                    </PermissionGate>
                  </div>
                </div>
              </Card>
            ),
          }
        ]}
      />
    </AdminSectionPage>
  );
}

export default Settings;
